/**
 * Data Consistency Checker (Development Only)
 * 
 * Validates that dashboard metrics match the underlying data
 * Helps catch inconsistencies during development
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { getGlobalDataStore } from '@/lib/mock-data/global-data-store';
import { useHomepageMetrics } from '@/hooks/useHomepageMetrics';

interface ValidationResult {
  name: string;
  passed: boolean;
  expected: string | number;
  actual: string | number;
  message?: string;
}

export function DataConsistencyChecker() {
  const [results, setResults] = useState<ValidationResult[]>([]);
  const [showChecker, setShowChecker] = useState(false);
  const metrics = useHomepageMetrics();

  // Only show in development
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const runChecks = () => {
    const store = getGlobalDataStore();
    const checks: ValidationResult[] = [];

    // Check 1: Promises Due Today
    const promisesDueToday = store.getPromisesDueToday();
    checks.push({
      name: 'Promises Due Today - Count',
      passed: metrics.todaysPriorities.promisesDueToday.count === promisesDueToday.length,
      expected: promisesDueToday.length,
      actual: metrics.todaysPriorities.promisesDueToday.count,
    });

    // Check 2: Cases Due Today
    const casesDueToday = store.getCasesDueToday();
    checks.push({
      name: 'Cases Due Today - Count',
      passed: metrics.todaysPriorities.casesDueToday.count === casesDueToday.length,
      expected: casesDueToday.length,
      actual: metrics.todaysPriorities.casesDueToday.count,
    });

    // Check 3: Unallocated Receipts
    const unallocatedReceipts = store.getUnallocatedReceipts();
    checks.push({
      name: 'Unallocated Receipts - Count',
      passed: metrics.todaysPriorities.unallocatedReceipts.count === unallocatedReceipts.length,
      expected: unallocatedReceipts.length,
      actual: metrics.todaysPriorities.unallocatedReceipts.count,
    });

    const unallocatedValue = unallocatedReceipts.reduce((sum, r) => sum + r.unappliedAmount, 0);
    checks.push({
      name: 'Unallocated Receipts - Value',
      passed: Math.abs(metrics.todaysPriorities.unallocatedReceipts.value - unallocatedValue) < 1,
      expected: `£${unallocatedValue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}`,
      actual: `£${metrics.todaysPriorities.unallocatedReceipts.value.toLocaleString('en-GB', { minimumFractionDigits: 2 })}`,
    });

    // Check 4: Disputes Requiring Response
    const disputesRequiringResponse = store.getDisputesRequiringResponseToday();
    checks.push({
      name: 'Disputes Requiring Response - Count',
      passed: metrics.todaysPriorities.disputesRequiringResponse.count === disputesRequiringResponse.length,
      expected: disputesRequiringResponse.length,
      actual: metrics.todaysPriorities.disputesRequiringResponse.count,
    });

    // Check 5: Payments Received Today
    const paymentsReceivedToday = store.getReceiptsReceivedToday();
    checks.push({
      name: 'Payments Received Today - Count',
      passed: metrics.todaysWins.paymentsReceived.count === paymentsReceivedToday.length,
      expected: paymentsReceivedToday.length,
      actual: metrics.todaysWins.paymentsReceived.count,
    });

    const paymentsValue = paymentsReceivedToday.reduce((sum, r) => sum + r.receivedAmount, 0);
    checks.push({
      name: 'Payments Received Today - Value',
      passed: Math.abs(metrics.todaysWins.paymentsReceived.value - paymentsValue) < 1,
      expected: `£${paymentsValue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}`,
      actual: `£${metrics.todaysWins.paymentsReceived.value.toLocaleString('en-GB', { minimumFractionDigits: 2 })}`,
    });

    // Check 6: Promises Kept Today
    const promisesKeptToday = store.getPromisesKeptToday();
    checks.push({
      name: 'Promises Kept Today - Count',
      passed: metrics.todaysWins.promisesKept.count === promisesKeptToday.length,
      expected: promisesKeptToday.length,
      actual: metrics.todaysWins.promisesKept.count,
    });

    // Check 7: Cases Resolved Today
    const casesResolvedToday = store.getCasesResolvedToday();
    checks.push({
      name: 'Cases Resolved Today - Count',
      passed: metrics.todaysWins.casesResolved.count === casesResolvedToday.length,
      expected: casesResolvedToday.length,
      actual: metrics.todaysWins.casesResolved.count,
    });

    // Check 8: Promise Success Rate
    const allPromises = store.getAllPromises();
    const activeAndCompletedPromises = allPromises.filter(
      p => p.status === 'Active' || p.status === 'Paid' || p.status === 'Partially Paid' || p.status === 'Broken'
    );
    const successfulPromises = allPromises.filter(p => p.status === 'Paid' || p.status === 'Partially Paid');
    const expectedSuccessRate = activeAndCompletedPromises.length > 0
      ? Math.round((successfulPromises.length / activeAndCompletedPromises.length) * 100)
      : 0;
    
    checks.push({
      name: 'Promise Success Rate',
      passed: metrics.keyMetrics.promiseSuccessRate === expectedSuccessRate,
      expected: `${expectedSuccessRate}%`,
      actual: `${metrics.keyMetrics.promiseSuccessRate}%`,
      message: `${successfulPromises.length} kept / ${activeAndCompletedPromises.length} total`,
    });

    setResults(checks);
    setShowChecker(true);

    // Also log to console
    console.group('📊 Data Consistency Check Results');
    checks.forEach((check) => {
      const icon = check.passed ? '✅' : '❌';
      console.log(`${icon} ${check.name}: Expected ${check.expected}, Got ${check.actual}`);
      if (check.message) {
        console.log(`   ${check.message}`);
      }
    });
    const passedCount = checks.filter(c => c.passed).length;
    console.log(`\n${passedCount}/${checks.length} checks passed`);
    console.groupEnd();
  };

  const passedCount = results.filter(r => r.passed).length;
  const failedCount = results.filter(r => !r.passed).length;

  return (
    <>
      {/* Floating Button */}
      <Button
        onClick={runChecks}
        className="fixed bottom-4 right-4 z-50 shadow-lg"
        variant="outline"
        size="sm"
      >
        <AlertCircle className="h-4 w-4 mr-2" />
        Check Data Consistency
      </Button>

      {/* Results Modal */}
      {showChecker && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Data Consistency Check Results</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowChecker(false)}
                >
                  Close
                </Button>
              </div>
              <div className="flex gap-4 mt-2">
                <Badge variant="default" className="bg-green-500">
                  {passedCount} Passed
                </Badge>
                {failedCount > 0 && (
                  <Badge variant="destructive">
                    {failedCount} Failed
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {results.map((result, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border ${
                      result.passed
                        ? 'bg-green-50 border-green-200'
                        : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {result.passed ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <div className="font-medium text-sm">{result.name}</div>
                        <div className="text-xs text-gray-600 mt-1">
                          Expected: <span className="font-mono">{result.expected}</span>
                          {' | '}
                          Actual: <span className="font-mono">{result.actual}</span>
                        </div>
                        {result.message && (
                          <div className="text-xs text-gray-500 mt-1">
                            {result.message}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
